/**
 * @module lib/print/printShopPdf
 * @description Builds a print-shop-ready PDF from rendered card faces:
 * one face per page, final size plus bleed, 300 dpi, in print order
 * (front, back, front, back…), which is how shops impose double-sided cards.
 *
 * Bleed: every card face has a flat paper-colored background at its edges,
 * so the bleed is filled with the face's own background color. Nothing
 * meaningful is lost if the cutter drifts by up to 0.125 in.
 *
 * External integration: jsPDF (loaded on demand, so the public pages do not
 * download it) and html-to-image via outputEngine.renderElementToCanvas.
 */
import { BLEED_IN, PRINT_DPI, type PrintSize } from "../../config/print";
import { renderElementToCanvas } from "../outputEngine";

export interface PrintShopPdfOptions {
    size: PrintSize;
    /** Adds BLEED_IN on every side (print shops). Off = exact trim size (proofs, home printers). */
    bleed: boolean;
    /** Progress callback for the UI: (done, total). */
    onProgress?: (done: number, total: number) => void;
}

/** Places a trim-size render onto a bleed-size canvas filled with the face's edge color. */
function withBleed(trim: HTMLCanvasElement, bleedPx: number, fill: string): HTMLCanvasElement {
    if (bleedPx === 0) return trim;
    const canvas = document.createElement("canvas");
    canvas.width = trim.width + bleedPx * 2;
    canvas.height = trim.height + bleedPx * 2;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas is unavailable in this browser.");
    ctx.fillStyle = fill;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(trim, bleedPx, bleedPx);
    return canvas;
}

/**
 * @param faces - Rendered card faces in print order. Each must be laid out
 *   (in the DOM, not display:none) at any width; it is captured at 300 dpi.
 */
export async function buildPrintShopPdf(faces: readonly HTMLElement[], { size, bleed, onProgress }: PrintShopPdfOptions): Promise<Blob> {
    if (faces.length === 0) throw new Error("Nothing selected to print.");
    const { jsPDF } = await import("jspdf");
    const bleedIn = bleed ? BLEED_IN : 0;
    const pageW = size.widthIn + bleedIn * 2;
    const pageH = size.heightIn + bleedIn * 2;
    const pdf = new jsPDF({ unit: "in", format: [pageW, pageH], orientation: "landscape", compress: true });

    for (let i = 0; i < faces.length; i++) {
        const face = faces[i];
        const trim = await renderElementToCanvas(face, Math.round(size.widthIn * PRINT_DPI));
        // The face may be wrapped (e.g. a PROOF overlay); the bleed takes the card surface's color.
        const surface = face.matches(".postcard") ? face : (face.querySelector<HTMLElement>(".postcard") ?? face);
        const fill = getComputedStyle(surface).backgroundColor || "#ffffff";
        const page = withBleed(trim, Math.round(bleedIn * PRINT_DPI), fill);
        if (i > 0) pdf.addPage([pageW, pageH], "landscape");
        // JPEG at high quality keeps a 22-card, double-sided kit to a size print portals accept.
        pdf.addImage(page.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, pageW, pageH, undefined, "FAST");
        onProgress?.(i + 1, faces.length);
    }
    pdf.setProperties({ title: "To Jamaica, With Love — print file", creator: "The American Friends of Jamaica" });
    return pdf.output("blob");
}
